import { Bluetooth, CircleCheck, ImageIcon, Scroll } from "@/components/icons";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  cn,
  Separator,
  Skeleton,
  Spinner,
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/kit";
import {
  CONTENT_MODE_OPTIONS,
  type LuckContentMode,
} from "@/modules/chat-tools/domain/luck-printer/content-modes";
import type { LuckPrinterDensity } from "@/modules/chat-tools/domain/luck-printer/protocol";

const DENSITY_OPTIONS: { value: LuckPrinterDensity; label: string }[] = [
  { value: 0, label: "ເບົາ" },
  { value: 1, label: "ປົກກະຕິ" },
  { value: 2, label: "ເຂັ້ມ" },
];

export type PrinterConnectionState = "connected" | "saved" | "none";

type PrinterDeviceCardProps = {
  deviceLabel: string;
  connectionState: PrinterConnectionState;
  isConnecting: boolean;
  canForget: boolean;
  onConnect: () => void;
  onForget: () => void;
  disabled?: boolean;
};

export function PrinterDeviceCard({
  deviceLabel,
  connectionState,
  isConnecting,
  canForget,
  onConnect,
  onForget,
  disabled = false,
}: PrinterDeviceCardProps) {
  const statusBadge =
    connectionState === "connected"
      ? { variant: "success" as const, label: "ເຊື່ອມຕໍ່ແລ້ວ" }
      : connectionState === "saved"
        ? { variant: "secondary" as const, label: "ຈື່ໄວ້" }
        : { variant: "outline" as const, label: "ຍັງບໍ່ເຊື່ອມຕໍ່" };

  const hint =
    connectionState === "connected"
      ? "ພ້ອມພິມ — ກົດປຸ່ມດ້ານລຸ່ມ"
      : connectionState === "saved"
        ? "ກົດພິມເພື່ອເຊື່ອມຕໍ່ອັດຕະໂນມັດ"
        : "ເລືອກເຄື່ອງພິມ Bluetooth ກ່ອນ";

  return (
    <div className="rounded-xl border border-secondary/20 bg-muted/20 p-3">
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full",
            connectionState === "connected"
              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
              : "bg-background text-muted-foreground",
          )}
        >
          {connectionState === "connected" ? (
            <CircleCheck aria-hidden />
          ) : (
            <Bluetooth aria-hidden />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-sm">{deviceLabel}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Badge variant={statusBadge.variant} className="rounded-full">
              {statusBadge.label}
            </Badge>
          </div>
          <p className="mt-1.5 text-muted-foreground text-xs">{hint}</p>
        </div>
      </div>

      <Separator className="my-3 bg-secondary/20" />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant={connectionState === "none" ? "default" : "outline"}
          size="sm"
          className="rounded-full"
          disabled={disabled || isConnecting}
          isLoading={isConnecting}
          onClick={onConnect}
        >
          <Bluetooth data-icon="inline-start" />
          {connectionState === "connected" ? "ເລືອກໃໝ່" : "ເຊື່ອມຕໍ່ເຄື່ອງ"}
        </Button>
        {canForget ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="rounded-full"
            disabled={disabled || isConnecting}
            onClick={onForget}
          >
            ລືມເຄື່ອງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}

type PrinterPreviewPanelProps = {
  previewUrl: string | null;
  isLoading: boolean;
  alt?: string;
};

export function PrinterPreviewPanel({
  previewUrl,
  isLoading,
  alt = "ຕົວຢ່າງປ້າຍ",
}: PrinterPreviewPanelProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm">ຕົວຢ່າງປ້າຍ</span>
        {isLoading ? (
          <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
            <Spinner />
            ກຳລັງໂຫຼດ...
          </span>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-secondary/20 bg-white p-2">
        {isLoading ? (
          <Skeleton className="mx-auto aspect-384/520 max-h-44 w-full rounded-lg" />
        ) : previewUrl ? (
          <img
            src={previewUrl}
            alt={alt}
            className="mx-auto max-h-44 w-full object-contain"
          />
        ) : (
          <div className="flex aspect-384/520 max-h-44 items-center justify-center rounded-lg bg-muted/30 px-4 text-center text-muted-foreground text-xs">
            ບໍ່ສາມາດໂຫຼດຕົວຢ່າງໄດ້
          </div>
        )}
      </div>
    </div>
  );
}

type PrinterContentModeFieldProps = {
  value: LuckContentMode;
  onChange: (value: LuckContentMode) => void;
  disabled?: boolean;
};

export function PrinterContentModeField({
  value,
  onChange,
  disabled = false,
}: PrinterContentModeFieldProps) {
  const selected =
    CONTENT_MODE_OPTIONS.find((option) => option.value === value) ??
    CONTENT_MODE_OPTIONS[0];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm">ຮູບແບບປ້າຍ</span>
        <Badge variant="secondary" className="rounded-full">
          Image ແນະນຳ
        </Badge>
      </div>

      <ToggleGroup
        type="single"
        value={value}
        onValueChange={(next) => next && onChange(next as LuckContentMode)}
        variant="outline"
        size="sm"
        className="grid w-full grid-cols-2"
        disabled={disabled}
      >
        <ToggleGroupItem
          value="image"
          className="rounded-full"
          aria-label="Image mode"
        >
          <ImageIcon data-icon="inline-start" />
          Image
        </ToggleGroupItem>
        <ToggleGroupItem
          value="text"
          className="rounded-full"
          aria-label="Text mode"
        >
          <Scroll data-icon="inline-start" />
          Text
        </ToggleGroupItem>
      </ToggleGroup>

      <p className="text-muted-foreground text-xs">{selected.hint}</p>
    </div>
  );
}

type PrinterDensityFieldProps = {
  value: LuckPrinterDensity;
  onChange: (value: LuckPrinterDensity) => void;
  disabled?: boolean;
};

export function PrinterDensityField({
  value,
  onChange,
  disabled = false,
}: PrinterDensityFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-medium text-sm">ຄວາມເຂັ້ມ</span>
      <ToggleGroup
        type="single"
        value={String(value)}
        onValueChange={(next) =>
          next && onChange(Number(next) as LuckPrinterDensity)
        }
        variant="outline"
        size="sm"
        className="grid w-full grid-cols-3"
        disabled={disabled}
      >
        {DENSITY_OPTIONS.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={String(option.value)}
            className="rounded-full px-2 text-xs sm:px-3 sm:text-sm"
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

type PrinterStatusBannerProps = {
  status: string | null;
  isBusy?: boolean;
  variant?: "default" | "destructive";
};

export function PrinterStatusBanner({
  status,
  isBusy = false,
  variant = "default",
}: PrinterStatusBannerProps) {
  if (!status) return null;

  return (
    <Alert variant={variant} className="border-secondary/20 bg-muted/20">
      {isBusy ? <Spinner /> : null}
      <AlertDescription>{status}</AlertDescription>
    </Alert>
  );
}
